import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/ui/PasswordInput";
import { Label } from "@/components/ui/label";
import { useAuth } from '@/hooks/useAuth';
import { Loader2, Building2, ArrowLeft, FileText, Shield, Ban, Mail } from 'lucide-react';
import { Link } from 'react-router-dom';
import { AccessRequestForm } from '@/components/AccessRequestForm';

const Auth = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [showAccessRequest, setShowAccessRequest] = useState(false);
  const { user, signIn } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (user) {
      navigate('/dashboard');
    }
  }, [user, navigate]);

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    
    const { error } = await signIn(email, password);
    
    if (!error) {
      navigate('/dashboard');
    }
    
    setLoading(false);
  };

  const handleAccessRequestSuccess = () => {
    setShowAccessRequest(false);
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Header */}
      <nav className="border-b border-border/40 p-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <button 
            onClick={() => navigate('/auth')} 
            className="flex items-center space-x-2 hover:text-primary transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            <span className="text-sm text-muted-foreground">Back to Login</span>
          </button>
          <div className="flex items-center space-x-2">
            <div className="w-8 h-8 gradient-primary rounded-lg flex items-center justify-center">
              <FileText className="h-5 w-5 text-white" />
            </div>
            <span className="text-xl font-bold bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent">
              BillPortal
            </span>
          </div>
        </div>
      </nav>

      {/* Auth Content */}
      <div className="flex-1 flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          {showAccessRequest ? (
            <AccessRequestForm onSuccess={handleAccessRequestSuccess} />
          ) : (
            <Card className="shadow-card border-border/50">
              <CardHeader className="text-center">
                <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mx-auto mb-4">
                  <Shield className="h-6 w-6 text-primary" />
                </div>
                <CardTitle className="text-2xl font-bold">BillPortal - Secure Access</CardTitle>
                <CardDescription>
                  Enterprise billing management platform
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Sign In Form */}
                <form onSubmit={handleSignIn} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="signin-email">Email</Label>
                    <Input
                      id="signin-email"
                      type="email"
                      placeholder="Enter your email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="signin-password">Password</Label>
                    <PasswordInput
                      id="signin-password"
                      placeholder="Enter your password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                    />
                    <div className="text-right">
                      <Link 
                        to="/reset-password" 
                        className="text-sm text-primary hover:underline"
                      >
                        Forgot password?
                      </Link>
                    </div>
                  </div>
                  <Button type="submit" className="w-full" disabled={loading}>
                    {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    Sign In
                  </Button>
                </form>

                {/* Security Notice */}
                <div className="border-t border-border pt-6">
                  <div className="bg-destructive/10 p-4 rounded-lg border border-destructive/20 space-y-3">
                    <div className="flex items-center gap-2">
                      <Ban className="h-5 w-5 text-destructive" />
                      <h3 className="font-semibold text-destructive">Public Registration Disabled</h3>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      BillPortal is an invitation-only platform. Access is restricted to authorized users 
                      who have received workspace invitations.
                    </p>
                  </div>

                  {/* Access Request Section */}
                  <div className="mt-4 space-y-3">
                    <div className="text-center">
                      <h4 className="font-medium">Need Access?</h4>
                      <p className="text-sm text-muted-foreground">
                        If you need access to BillPortal, submit an access request below.
                      </p>
                    </div>
                    
                    <Button 
                      variant="outline" 
                      className="w-full"
                      onClick={() => setShowAccessRequest(true)}
                    >
                      <Mail className="mr-2 h-4 w-4" />
                      Request Access
                    </Button>

                    <div className="text-center text-xs text-muted-foreground">
                      Our team will review and contact you within 2-3 business days.
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default Auth;